import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { auth, db, storage } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Cropper from "react-easy-crop";
import getCroppedImg from "../utils/cropImage";
import Loader from "../components/Loader";
import { useToast } from "../components/Toast";
import styles from "./MiPerfil.module.css";
import { FaUser, FaSave, FaPhone, FaIdCard, FaEnvelope, FaPencilAlt, FaCheckCircle, FaAddressCard } from "react-icons/fa";
import { useIsMobile } from "../hooks/useResponsive";
import RoleSwitcher from "../components/RoleSwitcher";
import CambiarPasswordCard from "../components/CambiarPasswordCard";
import { AppScreen, AppHeader, AppCard, AppButton, AppSpinner, SectionLabel, IconBox, Badge, SegmentedControl } from "../components/app";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../context/LanguageContext";
import { LANGUAGE_NAMES, SUPPORTED_LANGUAGES } from "../i18n";
import { appColors, appType, appSpacing, appRadius, appShadows } from "../utils/appTheme";
import { colors } from "../utils/designTokens";
import { normalizePhoneUY } from "../utils/phoneE164";
import { useEffectiveIdentity, useIsReadOnly } from "../context/EffectiveIdentityContext";
import { useBuilding } from "../context/BuildingContext";
import { fetchUsersByField, tolerantId } from "../utils/firestoreHelpers";
import { useAppDialog } from "../components/AppDialog";
import { updateMyContact, nuevoRequestId, mensajeDeError, codigoDeError } from "../utils/identityApi";

import { DOC_TYPES, normalizeDocumento, getDocumentoValidationMessage } from "../utils/documento";

// Fase 2: cambio de contraseña in-app. Encendido por default; se apaga con
// REACT_APP_FF_CAMBIO_CLAVE="false".
const CAMBIO_CLAVE_ON = process.env.REACT_APP_FF_CAMBIO_CLAVE !== "false";

/**
 * Ficha `users` propia y vigente, con el mismo criterio que el servidor
 * (identity/core.docsDelLlamador): ni archivada, ni fusionada, ni demo, ni
 * vinculada a otra cuenta. Sin uid (modo "ver como") no se mira authUid.
 */
const fichaVigente = (x, uid) =>
  !!x &&
  !x.archivedAt &&
  x.mergedIntoUserid == null &&
  x.isDemo !== true &&
  (!uid || !x.authUid || String(x.authUid) === String(uid));

/**
 * Fichas del residente que el staff mira en "Ver como residente". Con las
 * reglas 0D (C10) el staff no lista users por email: lee las fichas del userid
 * del residente con accessBids del edificio que está mirando
 * (fetchUsersByField: todas las fichas, sin valores por defecto, para elegir
 * la vigente como fuera de "ver como"). Sin userid o edificio, o si la regla lo
 * niega, no hay ficha y la pantalla muestra lo que trae el contexto (nombre y
 * email).
 *
 * @returns {Promise<Array<{ id: string, data: Record<string, any> }>>}
 */
async function fichasParaVerComo(userid, buildingId) {
  const presente = (v) => v != null && String(v).trim() !== "";
  if (!presente(userid) || !presente(buildingId)) return [];
  try {
    const fichas = await fetchUsersByField("userid", tolerantId(String(userid).trim()), buildingId);
    return (fichas || []).map(({ id, ...data }) => ({ id, data }));
  } catch (err) {
    console.warn("MiPerfil (ver como): no se pudo leer la ficha", err?.code || err?.message || err);
    return [];
  }
}

/** Tipo de documento conocido; cualquier otro valor cuenta como CI, igual que el servidor. */
const tipoDocumentoValido = (t) => (DOC_TYPES.includes(String(t || "").trim()) ? String(t).trim() : "CI");

const tieneTexto = (v) => String(v ?? "").trim() !== "";

/**
 * Mensaje para el usuario de un error de callable. Los del servidor ya vienen
 * en español; un error de red trae solo el código ("internal",
 * "deadline-exceeded"), y ahí va el texto genérico.
 */
const textoDeError = (err, fallback) => {
  const msg = mensajeDeError(err, fallback);
  return /^[a-z-]+$/.test(msg) ? fallback : msg;
};

// Campos del formulario que se comparan para saber si hay cambios sin guardar.
const CAMPOS_EDITABLES = ["NombreCompleto", "ApellidosCompleto", "tipoDocumento", "Documento", "celular"];

const MiPerfil = ({ setSelected, goBack }) => {
  const { showToast } = useToast();
  const { confirmDialog } = useAppDialog();
  const isMobile = useIsMobile();
  const { t } = useTranslation("miPerfil");
  const { t: tc } = useTranslation("common");
  const { language, setLanguage } = useLanguage();
  const languageOptions = SUPPORTED_LANGUAGES.map((l) => ({ value: l, label: LANGUAGE_NAMES[l] }));
  const { email: effEmail, userid: effUserid, viewingAs } = useEffectiveIdentity();
  // "Ver como residente": modo solo lectura. Bloquea guardar perfil y cambiar foto.
  const readOnly = useIsReadOnly();
  // Edificio que se está mirando en "ver como" (el BuildingProvider del residente).
  const { selectedBuilding } = useBuilding() || {};
  const bidVerComo = readOnly ? selectedBuilding?.buildingid ?? null : null;
  const nombreVerComo = readOnly ? viewingAs?.name || "" : "";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Freno contra un segundo envío (Enter) antes de que se re-renderice el botón deshabilitado.
  const savingRef = useRef(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [userDocId, setUserDocId] = useState(null);
  const fileInputRef = useRef(null);
  const [original, setOriginal] = useState(null);
  const [errors, setErrors] = useState({});
  const [phoneError, setPhoneError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [form, setForm] = useState({
    NombreCompleto: "",
    ApellidosCompleto: "",
    tipoDocumento: "CI",
    Documento: "",
    name: "",
    celular: "",
    email: "",
    photoURL: "",
  });

  // Perfil: la primera ficha vigente con el email (si no hay, la primera, como
  // useUserProfile para el header). Los textos se pasan a string: hay fichas
  // viejas con documento o celular numéricos.
  const cargarPerfil = useCallback(async () => {
    if (!effEmail) {
      setLoading(false);
      return;
    }

    try {
      let fichas;
      if (readOnly) {
        // "Ver como": las fichas del residente mirado, por userid y edificio
        // (C10). Primero las de su email, si tiene varias.
        const emailVerComo = String(effEmail).toLowerCase();
        fichas = (await fichasParaVerComo(effUserid, bidVerComo)).sort(
          (a, b) =>
            Number(String(a.data.email || "").toLowerCase() !== emailVerComo) -
            Number(String(b.data.email || "").toLowerCase() !== emailVerComo)
        );
      } else {
        const snap = await getDocs(query(collection(db, "users"), where("email", "==", effEmail)));
        fichas = snap.docs.map((s) => ({ id: s.id, data: s.data() }));
      }
      if (fichas.length === 0) {
        setForm((f) => ({
          ...f,
          email: effEmail,
          ...(nombreVerComo ? { NombreCompleto: nombreVerComo, name: nombreVerComo } : {}),
        }));
        return;
      }

      const uid = readOnly ? null : auth.currentUser?.uid;
      const vigentes = fichas.filter((f) => fichaVigente(f.data, uid));
      const elegida = vigentes[0] || fichas[0];
      const d = elegida.data;
      // El documento se carga una sola vez y vale para TODAS las fichas propias
      // (updateMyContact rechaza otro valor): si esta ficha no lo tiene y otra
      // sí, se muestra el de esa.
      const conDocumento = tieneTexto(d.Documento)
        ? d
        : vigentes.map((f) => f.data).find((x) => tieneTexto(x.Documento)) || d;
      const data = {
        NombreCompleto: String(d.NombreCompleto ?? d.name ?? ""),
        ApellidosCompleto: String(d.ApellidosCompleto ?? ""),
        tipoDocumento: tipoDocumentoValido(conDocumento.tipoDocumento),
        Documento: String(conDocumento.Documento ?? ""),
        name: String(d.name ?? d.NombreCompleto ?? ""),
        celular: String(d.celular ?? ""),
        email: d.email ?? effEmail,
        photoURL: d.photoURL ?? d.avatar ?? "",
      };
      setUserDocId(elegida.id);
      setForm(data);
      setOriginal(data);
    } catch (err) {
      console.error("Error al cargar perfil:", err);
    } finally {
      setLoading(false);
    }
  }, [effEmail, readOnly, effUserid, bidVerComo, nombreVerComo]);

  useEffect(() => {
    cargarPerfil();
  }, [cargarPerfil]);

  const isDirty = useMemo(() => {
    if (!original) return false;
    return CAMPOS_EDITABLES.some((k) => form[k] !== original[k]);
  }, [form, original]);

  // El documento ya cargado no se cambia desde acá: lo corrige la
  // administración (updateMyContact responde documento_cargado).
  const documentoBloqueado = tieneTexto(original?.Documento);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const validatePhone = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\s+/g, "");
    if (/^0?9\d{7}$/.test(cleaned) || /^\+598\s?9\d{7}$/.test(cleaned)) return "";
    return t("validation.phoneFormat");
  };

  const handleChange = (field, value) => {
    // Normalización tipo-aware: un pasaporte alfanumérico no pierde las letras.
    const nextValue =
      field === "Documento" ? normalizeDocumento(value, form.tipoDocumento || "CI") : value;
    setForm((f) => ({ ...f, [field]: nextValue }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: "" }));
    if (field === "celular") {
      setPhoneError(validatePhone(value));
    }
  };

  const handleImageClick = () => {
    if (readOnly) { showToast(t("toast.readOnly")); return; }
    fileInputRef.current?.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !userDocId) return;
    if (!file.type.startsWith("image/")) {
      showToast(t("toast.selectImage"), "warning");
      return;
    }
    const MAX_SIZE_MB = 5;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      showToast(t("toast.imageTooLarge", { max: MAX_SIZE_MB, size: (file.size / (1024 * 1024)).toFixed(1) }), "warning");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCropImage(reader.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleCropConfirm = async () => {
    if (readOnly) { showToast(t("toast.readOnly")); return; }
    if (!cropImage || !croppedAreaPixels || !userDocId) return;
    setUploadingImg(true);
    setCropImage(null);
    try {
      const croppedBlob = await getCroppedImg(cropImage, croppedAreaPixels);
      const path = `avatars/${userDocId}_${Date.now()}.jpg`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, croppedBlob);
      const url = await getDownloadURL(storageRef);
      setForm((f) => ({ ...f, photoURL: url }));
      await updateDoc(doc(db, "users", userDocId), { photoURL: url });
      window.dispatchEvent(new Event("habitia:profile-updated"));
      showToast(t("toast.photoUpdated"), "success");
    } catch (err) {
      console.error("Error al subir imagen:", err);
      showToast(t("toast.photoUploadError"), "error");
    } finally {
      setUploadingImg(false);
    }
  };

  const handleCropCancel = () => {
    setCropImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  /**
   * Documento, tipo y celular van por el servidor (updateMyContact), que los
   * escribe en todas las fichas propias. Confirmaciones del contrato: "teléfono
   * compartido" reenvía lo mismo con confirmarCompartido, y si los datos
   * cambiaron mientras se guardaba se reintenta una vez (el servidor relee
   * todo). El requestId es el del intento y se reusa en los reintentos.
   * Devuelve la respuesta, o null si la persona no confirmó.
   */
  const guardarContacto = async (payload, requestId) => {
    let extra = {};
    let reintentado = false;
    for (;;) {
      try {
        return await updateMyContact({ ...payload, ...extra, requestId });
      } catch (err) {
        const codigo = codigoDeError(err);
        const motivo = err?.details?.motivo;
        if (codigo === "failed-precondition" && motivo === "telefono_compartido" && !extra.confirmarCompartido) {
          const ok = await confirmDialog(textoDeError(err, t("toast.saveError")), {
            title: t("dialog.sharedPhoneTitle", "¿Es un teléfono compartido?"),
            confirmLabel: t("dialog.sharedPhoneConfirm", "Sí, es compartido"),
            cancelLabel: tc("cancel"),
          });
          if (!ok) return null;
          extra = { ...extra, confirmarCompartido: true };
          continue;
        }
        if (codigo === "aborted" && motivo === "datos_cambiaron" && !reintentado) {
          reintentado = true;
          continue;
        }
        throw err;
      }
    }
  };

  /** Error de updateMyContact: el mensaje del servidor, y en línea si es de un solo campo. */
  const mostrarErrorContacto = async (err, payload) => {
    console.error("Error al guardar documento/celular:", err);
    const codigo = codigoDeError(err);
    const motivo = err?.details?.motivo;
    const msg = textoDeError(err, t("toast.saveError"));
    if (codigo === "aborted" && motivo === "datos_cambiaron") {
      // Volvieron a cambiar en el medio del reintento: se recarga lo último.
      showToast(msg, "warning");
      await cargarPerfil();
      return;
    }
    const mandoCelular = payload.celular !== undefined;
    const mandoDocumento = payload.documento !== undefined;
    if (
      motivo === "documento_en_uso" ||
      motivo === "documento_cargado" ||
      (codigo === "invalid-argument" && mandoDocumento && !mandoCelular)
    ) {
      setErrors((prev) => ({ ...prev, Documento: msg }));
    } else if (codigo === "invalid-argument" && mandoCelular && !mandoDocumento) {
      setPhoneError(msg);
    }
    showToast(msg, "error");
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (readOnly) { showToast(t("toast.readOnly")); return; }
    if (savingRef.current) return;
    if (!userDocId || !original) {
      showToast(t("toast.userNotIdentified"), "error");
      return;
    }

    const newErrors = {};
    if (!form.NombreCompleto.trim()) newErrors.NombreCompleto = t("validation.required");
    if (!form.ApellidosCompleto.trim()) newErrors.ApellidosCompleto = t("validation.required");
    if (!form.tipoDocumento?.trim()) newErrors.tipoDocumento = t("validation.required");
    if (!form.Documento.trim()) newErrors.Documento = t("validation.required");
    if (!form.celular.trim()) newErrors.celular = t("validation.required");
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      showToast(t("toast.completeRequired"), "warning");
      return;
    }

    // Solo viaja lo que cambió, y el formato se valida solo en eso: un dato
    // viejo que hoy no pasaría la validación no frena guardar el nombre.
    const contacto = {};
    if (form.celular !== original.celular) {
      const digits = form.celular.replace(/\D/g, "");
      if (digits.length < 8) {
        setPhoneError(t("validation.phoneMinDigits"));
        return;
      }
      // BUG-57 fix: Validar formato de teléfono y CI
      const celular = normalizePhoneUY(form.celular.trim()) || form.celular.trim();
      const phoneErr = validatePhone(celular);
      if (phoneErr) {
        setPhoneError(phoneErr);
        showToast(phoneErr, "warning");
        return;
      }
      contacto.celular = celular;
    }
    if (
      !documentoBloqueado &&
      (form.Documento !== original.Documento || form.tipoDocumento !== original.tipoDocumento)
    ) {
      const tipo = form.tipoDocumento || "CI";
      const documento = normalizeDocumento(form.Documento, tipo);
      const docError = getDocumentoValidationMessage(tipo, documento);
      if (docError) {
        showToast(docError, "warning");
        return;
      }
      contacto.tipoDocumento = tipo;
      contacto.documento = documento;
    }
    const cambianNombres =
      form.NombreCompleto !== original.NombreCompleto ||
      form.ApellidosCompleto !== original.ApellidosCompleto;
    const nombre = form.NombreCompleto.trim();
    const apellidos = form.ApellidosCompleto.trim();
    const name = String(form.name ?? "").trim() || nombre;
    // Lo que se mandó: si la persona sigue escribiendo mientras se guarda, no se le pisa.
    const enviado = { ...form };

    savingRef.current = true;
    setSaving(true);
    try {
      const guardados = {};

      // 1) Documento y celular: servidor. Si falla o no se confirma, no se guarda nada.
      if (Object.keys(contacto).length > 0) {
        let res;
        try {
          res = await guardarContacto(contacto, nuevoRequestId());
        } catch (err) {
          await mostrarErrorContacto(err, contacto);
          return;
        }
        if (!res) {
          showToast(t("toast.notSaved", "No se guardaron los cambios."), "info");
          return;
        }
        // Valores normalizados por el servidor, para refrescar el form.
        const perfil = res.perfil || {};
        if (perfil.tipoDocumento !== undefined) guardados.tipoDocumento = perfil.tipoDocumento;
        if (perfil.documento !== undefined) guardados.Documento = String(perfil.documento ?? "");
        if (perfil.celular !== undefined) guardados.celular = String(perfil.celular ?? "");
      }

      // 2) Nombres: directo sobre la ficha. La foto se guarda aparte, al recortarla.
      let nombresOk = true;
      if (cambianNombres) {
        try {
          await updateDoc(doc(db, "users", userDocId), {
            NombreCompleto: nombre,
            ApellidosCompleto: apellidos,
            name,
          });
          Object.assign(guardados, { NombreCompleto: nombre, ApellidosCompleto: apellidos, name });
        } catch (err) {
          console.error("Error al guardar:", err);
          nombresOk = false;
        }
      }

      setOriginal((prev) => ({ ...prev, ...guardados }));
      setForm((cur) => {
        const next = { ...cur };
        Object.entries(guardados).forEach(([k, v]) => {
          if (cur[k] === enviado[k]) next[k] = v;
        });
        return next;
      });
      if (Object.keys(guardados).length > 0) {
        setErrors({});
        setPhoneError("");
      }

      if (!nombresOk) {
        showToast(t("toast.saveError"), "error");
        return;
      }
      if (cambianNombres) window.dispatchEvent(new Event("habitia:profile-updated"));
      showToast(t("toast.profileUpdated"), "success");
      setSuccessMsg(t("toast.changesSaved"));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  // Modales compartidos (overlay fixed) entre mobile y desktop — misma lógica/estado.
  const cropModal = cropImage && (
    <div className={styles.cropOverlay}>
      <div className={styles.cropContainer}>
        <div className={styles.cropArea}>
          <Cropper
            image={cropImage}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className={styles.cropControls}>
          <label className={styles.cropZoomLabel}>{t("cropModal.zoom")}</label>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className={styles.cropSlider}
          />
          <div className={styles.cropButtons}>
            <button
              type="button"
              onClick={handleCropCancel}
              className={styles.cropCancelBtn}
            >
              {tc("cancel")}
            </button>
            <button
              type="button"
              onClick={handleCropConfirm}
              className={styles.cropConfirmBtn}
            >
              {tc("confirm")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const previewModal = showPreview && form.photoURL && (
    <div
      style={{
        position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: "rgba(0,0,0,0.7)", zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer",
      }}
      onClick={() => setShowPreview(false)}
    >
      <img
        src={form.photoURL}
        alt={t("aria.profilePhoto")}
        style={{
          maxWidth: "90vw", maxHeight: "90vh",
          borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          objectFit: "contain",
        }}
        onClick={(e) => e.stopPropagation()}
      />
      <button
        style={{
          position: "absolute", top: 20, right: 20,
          width: 40, height: 40, borderRadius: "50%",
          backgroundColor: "rgba(255,255,255,0.2)", border: "none",
          color: "white", fontSize: 20, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
        onClick={() => setShowPreview(false)}
      >
        ✕
      </button>
    </div>
  );

  // ─── Versión mobile: estilo app "Radial" ────────────────────────────────────
  if (isMobile) {
    const fieldWrap = { display: "flex", flexDirection: "column", gap: 6 };
    const labelStyle = { ...appType.bodySm, color: appColors.ink2 };
    const inputStyle = (hasError) => ({
      width: "100%",
      boxSizing: "border-box",
      padding: "12px 14px",
      borderRadius: appRadius.sm,
      border: `1px solid ${hasError ? appColors.red : appColors.line}`,
      background: appColors.surface,
      color: appColors.ink,
      fontFamily: appType.fontFamily,
      fontSize: 14.5,
      fontWeight: 400,
      outline: "none",
      transition: "border-color .15s ease",
    });
    const errorStyle = { ...appType.caption, color: appColors.red };
    // Mismo aspecto que el email (no editable).
    const readonlyInputStyle = { ...inputStyle(false), background: appColors.surface2, color: appColors.ink2 };
    const focusOn = (e) => { e.currentTarget.style.borderColor = appColors.blue; };
    const focusOff = (hasError) => (e) => {
      e.currentTarget.style.borderColor = hasError ? appColors.red : appColors.line;
    };
    const displayName = form.NombreCompleto || form.name || t("fallback.user");
    const initials = (displayName || "U")
      .split(" ")
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join("");

    return (
      <AppScreen
        header={
          <AppHeader
            title={t("header.title")}
            subtitle={t("header.subtitle")}
            onBack={goBack}
          />
        }
      >
        {/* Banner hero azul: avatar grande + nombre + estado de edición */}
        <AppCard
          variant="blue"
          padding={20}
          style={{ marginBottom: appSpacing.section }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
              minWidth: 0,
            }}
          >
            <div style={{ position: "relative", width: 104, height: 104 }}>
              <div
                onClick={() => form.photoURL && setShowPreview(true)}
                title={form.photoURL ? t("photo.viewLarge") : ""}
                style={{
                  width: 104,
                  height: 104,
                  borderRadius: "50%",
                  overflow: "hidden",
                  background: "rgba(255,255,255,.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: appColors.onBlue,
                  fontFamily: appType.fontFamily,
                  fontSize: 38,
                  fontWeight: 400,
                  cursor: form.photoURL ? "pointer" : "default",
                  border: "3px solid rgba(255,255,255,.28)",
                  boxSizing: "border-box",
                }}
              >
                {form.photoURL ? (
                  <img
                    src={form.photoURL}
                    alt={t("aria.avatar")}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <span>{initials || "?"}</span>
                )}
              </div>
              <button
                type="button"
                onClick={handleImageClick}
                aria-label={t("photo.change")}
                title={t("photo.change")}
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: 0,
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  border: `2px solid ${appColors.blue}`,
                  background: appColors.surface,
                  color: appColors.blue,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  fontSize: 13,
                  boxShadow: appShadows.soft,
                }}
              >
                <FaPencilAlt />
              </button>
              {uploadingImg && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "50%",
                    background: appColors.scrim,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AppSpinner
                    size={28}
                    color={appColors.onBlue}
                    trackColor={appColors.line}
                    centered={false}
                  />
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              style={{ display: "none" }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  ...appType.cardTitle,
                  color: appColors.onBlue,
                  textAlign: "center",
                  overflowWrap: "anywhere",
                }}
              >
                {displayName}
              </span>
              {form.email && (
                <span
                  style={{
                    ...appType.bodySm,
                    color: appColors.onBlue,
                    opacity: 0.85,
                    textAlign: "center",
                    overflowWrap: "anywhere",
                  }}
                >
                  {form.email}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleImageClick}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "8px 14px",
                borderRadius: appRadius.pill,
                border: "1px solid rgba(255,255,255,.35)",
                background: "rgba(255,255,255,.16)",
                color: appColors.onBlue,
                fontFamily: appType.fontFamily,
                fontSize: 12.5,
                fontWeight: 400,
                cursor: "pointer",
              }}
            >
              <FaPencilAlt size={11} /> {t("photo.change")}
            </button>
          </div>
        </AppCard>

        <form
          onSubmit={handleSave}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {successMsg && (
            <AppCard
              padding={14}
              style={{ borderLeft: `3px solid ${appColors.green}`, marginBottom: appSpacing.section }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <IconBox
                  icon={<FaCheckCircle />}
                  size={34}
                  bg={appColors.surface2}
                  color={appColors.green}
                />
                <span style={{ ...appType.bodySm, color: appColors.green, flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                  {successMsg}
                </span>
                <button
                  type="button"
                  onClick={() => setSuccessMsg("")}
                  aria-label={tc("close")}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: appColors.ink3, lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
            </AppCard>
          )}

          {/* Sección: Datos personales */}
          <SectionLabel style={{ marginBottom: 12 }}>{t("sections.personalData")}</SectionLabel>
          <AppCard padding={16} style={{ marginBottom: appSpacing.section }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={fieldWrap}>
                <label htmlFor="perfil-nombre-m" style={labelStyle}>{t("fields.name").toLowerCase()} *</label>
                <input
                  id="perfil-nombre-m"
                  type="text"
                  style={inputStyle(!!errors.NombreCompleto)}
                  value={form.NombreCompleto}
                  onChange={(e) => handleChange("NombreCompleto", e.target.value)}
                  onFocus={focusOn}
                  onBlur={focusOff(!!errors.NombreCompleto)}
                  placeholder={t("placeholders.name")}
                />
                {errors.NombreCompleto && <span style={errorStyle}>{errors.NombreCompleto}</span>}
              </div>

              <div style={fieldWrap}>
                <label htmlFor="perfil-apellidos-m" style={labelStyle}>{t("fields.lastName").toLowerCase()} *</label>
                <input
                  id="perfil-apellidos-m"
                  type="text"
                  style={inputStyle(!!errors.ApellidosCompleto)}
                  value={form.ApellidosCompleto}
                  onChange={(e) => handleChange("ApellidosCompleto", e.target.value)}
                  onFocus={focusOn}
                  onBlur={focusOff(!!errors.ApellidosCompleto)}
                  placeholder={t("placeholders.lastName")}
                />
                {errors.ApellidosCompleto && <span style={errorStyle}>{errors.ApellidosCompleto}</span>}
              </div>
            </div>
          </AppCard>

          {/* Sección: Documento */}
          <SectionLabel style={{ marginBottom: 12 }}>{t("sections.document")}</SectionLabel>
          <AppCard padding={16} style={{ marginBottom: appSpacing.section }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, minWidth: 0 }}>
              <IconBox icon={<FaAddressCard />} size={40} />
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ ...appType.tileName }}>{t("id.title")}</span>
                <span style={{ ...appType.bodySm, overflowWrap: "anywhere" }}>
                  {t("id.subtitle")}
                </span>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={fieldWrap}>
                <label htmlFor="perfil-tipo-documento-m" style={labelStyle}>{t("fields.docType").toLowerCase()} *</label>
                <select
                  id="perfil-tipo-documento-m"
                  style={documentoBloqueado ? readonlyInputStyle : inputStyle(!!errors.tipoDocumento)}
                  value={form.tipoDocumento || "CI"}
                  onChange={(e) => handleChange("tipoDocumento", e.target.value)}
                  onFocus={documentoBloqueado ? undefined : focusOn}
                  onBlur={documentoBloqueado ? undefined : focusOff(!!errors.tipoDocumento)}
                  disabled={documentoBloqueado}
                  aria-describedby="perfil-documento-hint-m"
                >
                  {DOC_TYPES.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {errors.tipoDocumento && <span style={errorStyle}>{errors.tipoDocumento}</span>}
              </div>

              <div style={fieldWrap}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <label htmlFor="perfil-documento-m" style={{ ...labelStyle, margin: 0 }}>{t("fields.docId").toLowerCase()} *</label>
                  {documentoBloqueado && <Badge tone="ink3">{t("badges.notEditable")}</Badge>}
                </div>
                <input
                  id="perfil-documento-m"
                  type="text"
                  style={documentoBloqueado ? readonlyInputStyle : inputStyle(!!errors.Documento)}
                  value={form.Documento}
                  onChange={(e) => handleChange("Documento", e.target.value)}
                  onFocus={documentoBloqueado ? undefined : focusOn}
                  onBlur={documentoBloqueado ? undefined : focusOff(!!errors.Documento)}
                  placeholder={t("placeholders.docId")}
                  inputMode="numeric"
                  readOnly={documentoBloqueado}
                  aria-describedby="perfil-documento-hint-m"
                />
                {errors.Documento && <span style={errorStyle}>{errors.Documento}</span>}
                <span id="perfil-documento-hint-m" style={{ ...appType.caption }}>
                  {documentoBloqueado
                    ? t("hints.documentLocked", "Para corregirlo, pedíselo a la administración de tu edificio.")
                    : t("hints.documentOnce", "Revisalo antes de guardar: después solo lo puede corregir la administración.")}
                </span>
              </div>
            </div>
          </AppCard>

          {/* Sección: Contacto */}
          <SectionLabel style={{ marginBottom: 12 }}>{t("sections.contact")}</SectionLabel>
          <AppCard padding={16} style={{ marginBottom: appSpacing.section }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={fieldWrap}>
                <label htmlFor="perfil-celular-m" style={labelStyle}>{t("fields.phone").toLowerCase()} *</label>
                <input
                  id="perfil-celular-m"
                  type="tel"
                  style={inputStyle(!!errors.celular || !!phoneError)}
                  value={form.celular}
                  onChange={(e) => handleChange("celular", e.target.value)}
                  onFocus={focusOn}
                  onBlur={(e) => {
                    setPhoneError(validatePhone(form.celular));
                    focusOff(!!errors.celular || !!phoneError)(e);
                  }}
                  placeholder={t("placeholders.phone")}
                />
                {errors.celular && <span style={errorStyle}>{errors.celular}</span>}
                {phoneError && <span style={errorStyle}>{phoneError}</span>}
              </div>

              <div style={fieldWrap}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <label htmlFor="perfil-email-m" style={{ ...labelStyle, margin: 0 }}>email</label>
                  <Badge tone="ink3">No editable</Badge>
                </div>
                <input
                  id="perfil-email-m"
                  type="email"
                  style={{ ...inputStyle(false), background: appColors.surface2, color: appColors.ink2 }}
                  value={form.email}
                  readOnly
                />
                <span style={{ ...appType.caption }}>{t("hints.emailChange")}</span>
              </div>
            </div>
          </AppCard>

          <AppButton
            type="submit"
            block
            disabled={saving || !isDirty || readOnly}
            icon={<FaSave size={14} />}
          >
            {saving ? "Guardando…" : "Guardar cambios"}
          </AppButton>
          {!isDirty && !saving && (
            <span style={{ ...appType.caption, textAlign: "center", marginTop: 10, display: "block" }}>
              No hay cambios sin guardar
            </span>
          )}
        </form>

        {!readOnly && (
          <>
            <SectionLabel style={{ marginBottom: 12 }}>{tc("language.title")}</SectionLabel>
            <AppCard style={{ marginBottom: appSpacing.section }}>
              <span style={{ ...appType.bodySm, display: "block", marginBottom: 10 }}>{tc("language.subtitle")}</span>
              <SegmentedControl options={languageOptions} value={language} onChange={setLanguage} />
            </AppCard>
          </>
        )}

        {CAMBIO_CLAVE_ON && (
          <>
            <SectionLabel style={{ marginBottom: 12 }}>Seguridad</SectionLabel>
            <div style={{ marginBottom: appSpacing.section }}>
              <CambiarPasswordCard />
            </div>
          </>
        )}

        <RoleSwitcher variant="card" />

        {cropModal}
        {previewModal}
      </AppScreen>
    );
  }

  // ─── Versión desktop (existente) ─────────────────────────────────────────────
  return (
    <div className={styles.page}>
      {/* Header estándar de residente (tarjeta blanca + caja de icono azul 42x42) */}
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          padding: "16px 20px",
          marginBottom: "20px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
          border: "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          gap: "14px",
        }}
      >
        <div
          style={{
            width: "42px",
            height: "42px",
            borderRadius: "10px",
            backgroundColor: "#0080ff",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
          }}
        >
          <FaUser />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: "20px", fontWeight: 400, margin: 0, color: "#0080ff" }}>{t("header.title")}</h1>
          <p style={{ fontSize: "13px", color: "#6b7280", margin: "2px 0 0 0", fontWeight: 400 }}>
            {t("header.subtitle")}
          </p>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.avatarSection}>
          <div className={styles.avatarWrapper}>
            <div
              className={styles.avatar}
              onClick={() => form.photoURL && setShowPreview(true)}
              title={form.photoURL ? t("photo.viewLarge") : ""}
              style={{ cursor: form.photoURL ? "pointer" : "default" }}
            >
              {form.photoURL ? (
                <img src={form.photoURL} alt={t("aria.avatar")} className={styles.avatarImg} />
              ) : (
                <span>{form.NombreCompleto?.[0] || form.name?.[0] || "?"}</span>
              )}
            </div>
            <div
              className={styles.avatarEdit}
              onClick={handleImageClick}
              title="Cambiar foto"
              style={{ cursor: "pointer" }}
            >
              <FaPencilAlt />
            </div>
            {uploadingImg && <div className={styles.avatarOverlay}>...</div>}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className={styles.fileInput}
          />
          <p className={styles.avatarName}>
            {form.NombreCompleto || form.name || "Usuario"}
          </p>
        </div>

        <form onSubmit={handleSave} className={styles.form}>
          {successMsg && (
            <div style={{
              padding: "12px 16px",
              backgroundColor: colors.successLight,
              color: colors.successDark,
              borderRadius: 8,
              marginBottom: 16,
              border: `1px solid ${colors.success}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <span>{successMsg}</span>
              <button onClick={() => setSuccessMsg("")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18 }}>×</button>
            </div>
          )}
          <div className={styles.field}>
            <label htmlFor="perfil-nombre" className={styles.label}>
              <FaUser className={styles.fieldIcon} />
              Nombre<span className={styles.required}>*</span>
            </label>
            <input
              id="perfil-nombre"
              type="text"
              className={`${styles.input} ${errors.NombreCompleto ? styles.inputError : ""}`}
              value={form.NombreCompleto}
              onChange={(e) => handleChange("NombreCompleto", e.target.value)}
              placeholder="Tu nombre"
            />
            {errors.NombreCompleto && <span className={styles.errorText}>{errors.NombreCompleto}</span>}
          </div>

          <div className={styles.field}>
            <label htmlFor="perfil-apellidos" className={styles.label}>
              <FaUser className={styles.fieldIcon} />
              Apellidos<span className={styles.required}>*</span>
            </label>
            <input
              id="perfil-apellidos"
              type="text"
              className={`${styles.input} ${errors.ApellidosCompleto ? styles.inputError : ""}`}
              value={form.ApellidosCompleto}
              onChange={(e) => handleChange("ApellidosCompleto", e.target.value)}
              placeholder="Tus apellidos"
            />
            {errors.ApellidosCompleto && <span className={styles.errorText}>{errors.ApellidosCompleto}</span>}
          </div>

          <div className={styles.field}>
            <label htmlFor="perfil-tipo-documento" className={styles.label}>
              <FaIdCard className={styles.fieldIcon} />
              Tipo de documento<span className={styles.required}>*</span>
            </label>
            <select
              id="perfil-tipo-documento"
              className={`${styles.input} ${documentoBloqueado ? styles.inputReadonly : errors.tipoDocumento ? styles.inputError : ""}`}
              value={form.tipoDocumento || "CI"}
              onChange={(e) => handleChange("tipoDocumento", e.target.value)}
              disabled={documentoBloqueado}
              aria-describedby="perfil-documento-hint"
            >
              {DOC_TYPES.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {errors.tipoDocumento && <span className={styles.errorText}>{errors.tipoDocumento}</span>}
          </div>

          <div className={styles.field}>
            <label htmlFor="perfil-documento" className={styles.label}>
              <FaIdCard className={styles.fieldIcon} />
              Documento de identidad<span className={styles.required}>*</span>
            </label>
            <input
              id="perfil-documento"
              type="text"
              className={`${styles.input} ${documentoBloqueado ? styles.inputReadonly : errors.Documento ? styles.inputError : ""}`}
              value={form.Documento}
              onChange={(e) => handleChange("Documento", e.target.value)}
              placeholder="Ej: 12345678 (sin puntos ni guiones)"
              inputMode="numeric"
              readOnly={documentoBloqueado}
              aria-describedby="perfil-documento-hint"
            />
            {errors.Documento && <span className={styles.errorText}>{errors.Documento}</span>}
            <span id="perfil-documento-hint" className={styles.readonlyHint}>
              {documentoBloqueado
                ? t("hints.documentLocked", "Para corregirlo, pedíselo a la administración de tu edificio.")
                : t("hints.documentOnce", "Revisalo antes de guardar: después solo lo puede corregir la administración.")}
            </span>
          </div>

          <div className={styles.field}>
            <label htmlFor="perfil-celular" className={styles.label}>
              <FaPhone className={styles.fieldIcon} />
              Celular<span className={styles.required}>*</span>
            </label>
            <input
              id="perfil-celular"
              type="tel"
              className={`${styles.input} ${errors.celular ? styles.inputError : ""}`}
              value={form.celular}
              onChange={(e) => handleChange("celular", e.target.value)}
              onBlur={() => setPhoneError(validatePhone(form.celular))}
              placeholder="099 123 456"
            />
            {errors.celular && <span className={styles.errorText}>{errors.celular}</span>}
            {phoneError && <span className={styles.errorText}>{phoneError}</span>}
          </div>

          <div className={styles.field}>
            <label htmlFor="perfil-email" className={styles.label}>
              <FaEnvelope className={styles.fieldIcon} />
              Email
            </label>
            <input
              id="perfil-email"
              type="email"
              className={`${styles.input} ${styles.inputReadonly}`}
              value={form.email}
              readOnly
            />
            <span className={styles.readonlyHint}>No se puede modificar</span>
          </div>

          <button
            type="submit"
            className={styles.saveBtn}
            disabled={saving || !isDirty || readOnly}
          >
            <FaSave />
            {saving ? "Guardando…" : "Guardar"}
          </button>
        </form>
      </div>

      {!readOnly && (
        <div style={{ maxWidth: 640, margin: "16px auto 0", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
          <h3 style={{ fontSize: 15, fontWeight: 400, color: colors.primary, margin: "0 0 4px" }}>{tc("language.title")}</h3>
          <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 14px" }}>{tc("language.subtitle")}</p>
          <div style={{ maxWidth: 360 }}>
            <SegmentedControl options={languageOptions} value={language} onChange={setLanguage} />
          </div>
        </div>
      )}

      {CAMBIO_CLAVE_ON && (
        <div style={{ maxWidth: 640, margin: "16px auto 0" }}>
          <CambiarPasswordCard />
        </div>
      )}

      <div style={{ maxWidth: 640, margin: "16px auto 0" }}>
        <RoleSwitcher variant="card" />
      </div>

      {/* Crop modal */}
      {cropModal}

      {/* Preview modal */}
      {previewModal}
    </div>
  );
};

export default MiPerfil;
